// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides API functions for the organization's needs-attention
 * threshold settings. Each configurable entity/reason pair carries the shipped
 * default day counts and an optional account-level override; reasons without an
 * override resolve to the default in attention computation.
 */
import {
  type AttentionThresholds,
  type UpdateAttentionThresholdsInput,
  attentionThresholdsSchema,
} from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

const attentionThresholdsResponseSchema = z.object({
  data: attentionThresholdsSchema,
  metadata: responseMetadataSchema,
});

/**
 * Retrieves the needs-attention threshold settings for an organization.
 *
 * @param orgKey - The unique key identifying the organization
 * @returns Promise resolving to the threshold settings
 * @throws Error if the API request fails or response validation fails
 */
export async function getAttentionThresholds(
  orgKey: string
): Promise<AttentionThresholds> {
  const path = `/v1/organization/${orgKey}/attention-thresholds`;
  const response = await api.get(path);
  const result = await parseResponse(
    attentionThresholdsResponseSchema,
    response.data,
    {
      schemaName: 'AttentionThresholds',
      endpoint: path,
    }
  );
  return result.data;
}

/**
 * Replaces the organization's needs-attention threshold override set.
 *
 * @param orgKey - The unique key identifying the organization
 * @param input - The full override set to store
 * @returns Promise resolving to the updated threshold settings
 * @throws Error if the API request fails or response validation fails
 */
export async function updateAttentionThresholds(
  orgKey: string,
  input: UpdateAttentionThresholdsInput
): Promise<AttentionThresholds> {
  const path = `/v1/organization/${orgKey}/attention-thresholds`;
  const response = await api.put(path, input);
  const result = await parseResponse(
    attentionThresholdsResponseSchema,
    response.data,
    {
      schemaName: 'UpdateAttentionThresholds',
      endpoint: path,
    }
  );
  return result.data;
}
